import { TaskHelpers } from 'ontraccr-common';

import { roundTotalRuntime } from '../time';

const isGoodTime = (startTime, endTime, shouldIncludeZeroTime) => (
  startTime
  && endTime
  && (endTime > startTime + 1000 || shouldIncludeZeroTime)
);

export const isGoodTask = (task, shouldIncludeZeroTimePTO) => (
  isGoodTime(task.startTime, task.endTime)
  || isGoodTime(task.otStartTime, task.otEndTime)
  || isGoodTime(task.breakStartTime, task.breakEndTime)
  || isGoodTime(task.doubleOTStartTime, task.doubleOTEndTime)
  || isGoodTime(task.ptoStartTime, task.ptoEndTime, shouldIncludeZeroTimePTO)
);

const WORK_BUCKET_KEYS = [
  'regularTime',
  'overtime',
  'doubleOT',
  'saturdayTime',
  'saturdayOT',
  'saturdayDoubleOT',
  'sundayTime',
  'sundayOT',
  'sundayDoubleOT',
];

const REGULAR_BUCKET_KEYS = ['regularTime', 'saturdayTime', 'sundayTime'];
const OT_BUCKET_KEYS = ['overtime', 'saturdayOT', 'sundayOT'];

const sumWorkBucketMs = (task) => {
  let total = 0;
  WORK_BUCKET_KEYS.forEach((key) => {
    total += task[key] || 0;
  });
  return total;
};

const getTaskRegularMs = (task) => {
  let total = 0;
  REGULAR_BUCKET_KEYS.forEach((key) => {
    total += task[key] || 0;
  });
  return total;
};

const getTaskOvertimeMs = (task) => {
  let total = 0;
  OT_BUCKET_KEYS.forEach((key) => {
    total += task[key] || 0;
  });
  return total;
};

const findFirstPositiveBucketKey = (task, keys) => keys.find((key) => task[key] > 0);

/** Mirrors backend roundDaysTask adjustTaskTime — bucket ms instead of timestamps. */
const adjustTaskBuckets = ({ task, regularTimeToAdjust, overtimeToAdjust }) => {
  const result = {
    adjustedRegularTime: 0,
    adjustedOvertime: 0,
  };
  const newTask = { ...task };

  if (regularTimeToAdjust > 0 && getTaskRegularMs(newTask) > 0) {
    const key = findFirstPositiveBucketKey(newTask, REGULAR_BUCKET_KEYS);
    newTask[key] += regularTimeToAdjust;
    result.adjustedRegularTime = regularTimeToAdjust;
  }

  if (regularTimeToAdjust < 0 && getTaskRegularMs(newTask) > 0) {
    const regularMs = getTaskRegularMs(newTask);
    if (regularMs <= -regularTimeToAdjust) {
      result.adjustedRegularTime = -regularMs;
      REGULAR_BUCKET_KEYS.forEach((key) => {
        newTask[key] = 0;
      });
    } else {
      const key = findFirstPositiveBucketKey(newTask, REGULAR_BUCKET_KEYS);
      newTask[key] += regularTimeToAdjust;
      result.adjustedRegularTime = regularTimeToAdjust;
    }
  }

  if (overtimeToAdjust > 0 && getTaskOvertimeMs(newTask) > 0) {
    const key = findFirstPositiveBucketKey(newTask, OT_BUCKET_KEYS);
    newTask[key] += overtimeToAdjust;
    result.adjustedOvertime = overtimeToAdjust;
  }

  if (overtimeToAdjust < 0 && getTaskOvertimeMs(newTask) > 0) {
    const overtimeMs = getTaskOvertimeMs(newTask);
    if (overtimeMs <= -overtimeToAdjust) {
      result.adjustedOvertime = -overtimeMs;
      OT_BUCKET_KEYS.forEach((key) => {
        newTask[key] = 0;
      });
    } else {
      const key = findFirstPositiveBucketKey(newTask, OT_BUCKET_KEYS);
      newTask[key] += overtimeToAdjust;
      result.adjustedOvertime = overtimeToAdjust;
    }
  }

  return { ...result, newTask };
};

const getRoundedDayTotalMs = (totalMs, settings = {}) => {
  const {
    roundingInterval,
    roundingType,
    roundingSetting,
  } = settings;

  if (roundingType === 'taskTime') {
    return TaskHelpers.getRoundedRuntime(
      totalMs,
      { roundingInterval, roundingSetting },
    );
  }

  return roundTotalRuntime(
    totalMs,
    roundingInterval,
    roundingType,
    roundingSetting,
  );
};

/**
 * Manual OT + overallTime read-path rounding (mirrors backend roundDaysTask).
 * Automatic OT companies get day rounding persisted on write; skip here.
 */
const applyDayBucketRounding = (tasks, settings, { isRounded = true } = {}) => {
  const {
    enableManualOT,
    roundingInterval,
    roundingSetting,
  } = settings ?? {};

  if (
    !isRounded
    || !enableManualOT
    || !roundingInterval
    || !roundingSetting
    || !tasks.length
  ) {
    return tasks;
  }

  // Group tasks by calendar day — rounding applies to each days total separately.
  const byDay = {};
  tasks.forEach((task) => {
    const dateKey = TaskHelpers.getTaskDate(task)?.toISODate() ?? 'unknown';
    if (!byDay[dateKey]) byDay[dateKey] = [];
    byDay[dateKey].push(task);
  });

  const result = [];
  Object.values(byDay).forEach((dayTasks) => {
    // Chronological order to modify last task first
    const sortedDayTasks = [...dayTasks].sort(
      (a, b) => TaskHelpers.getStartTime(a) - TaskHelpers.getStartTime(b),
    );

    let totalMs = 0;
    sortedDayTasks.forEach((task) => {
      totalMs += sumWorkBucketMs(task);
    });

    // Round the whole day to the configured interval.
    const roundedMs = getRoundedDayTotalMs(totalMs, settings);

    if (roundedMs === totalMs || !totalMs) {
      result.push(...sortedDayTasks);
      return;
    }

    const currentDayTasks = sortedDayTasks.map((task) => ({ ...task }));
    let toAdd = roundedMs - totalMs;
    let i = currentDayTasks.length - 1;

    let dayRegularMs = 0;
    let dayOvertimeMs = 0;
    currentDayTasks.forEach((task) => {
      dayRegularMs += getTaskRegularMs(task);
      dayOvertimeMs += getTaskOvertimeMs(task);
    });

    // Default: put the delta on regular time
    let regularTimeToAdjust = toAdd;
    let overtimeToAdjust = 0;

    if (dayRegularMs === 0 && dayOvertimeMs > 0) {
      // OT-only day: apply the full delta to overtime buckets.
      overtimeToAdjust = toAdd;
      regularTimeToAdjust = 0;
    } else if (toAdd < 0) {
      // Round down: peel overtime first; spill remainder into regular if OT is insufficient.
      overtimeToAdjust = toAdd;
      regularTimeToAdjust = 0;
      if (dayOvertimeMs < -toAdd) {
        regularTimeToAdjust = dayOvertimeMs + toAdd;
        overtimeToAdjust = -dayOvertimeMs;
      }
    }

    if (toAdd > 0) {
      // Rounding up: add ms to the last task(s) until the day total matches roundedMs.
      while (i >= 0 && toAdd > 0) {
        const {
          newTask,
          adjustedRegularTime,
          adjustedOvertime,
        } = adjustTaskBuckets({
          task: currentDayTasks[i],
          regularTimeToAdjust,
          overtimeToAdjust,
        });

        // adjustTaskBuckets returns positive amounts when adding; subtract from remaining delta.
        toAdd -= (adjustedRegularTime + adjustedOvertime);
        currentDayTasks[i] = newTask;
        i -= 1;
      }
    } else {
      // Rounding down: peel ms off the last task(s) until the day total matches roundedMs.
      let toRemove = -toAdd; // toAdd is negative when rounding down; track remaining ms as a positive value
      while (i >= 0 && toRemove > 0) {
        const {
          newTask,
          adjustedRegularTime,
          adjustedOvertime,
        } = adjustTaskBuckets({
          task: currentDayTasks[i],
          regularTimeToAdjust,
          overtimeToAdjust,
        });

        // adjustTaskBuckets returns negative amounts when removing; += reduces toRemove toward zero.
        toRemove += (adjustedRegularTime + adjustedOvertime);
        // Budgets start negative when rounding down; adjusted* are negative too, so -= moves toward zero.
        regularTimeToAdjust -= adjustedRegularTime;
        overtimeToAdjust -= adjustedOvertime;
        currentDayTasks[i] = newTask;
        i -= 1;
      }
    }

    result.push(...currentDayTasks);
  });

  return result;
};

/**
 * When viewing Actual on task-time companies, use stored original timestamps.
 */
export const resolveTaskDisplayTimes = (task, { isRounded = true, roundingType } = {}) => {
  if (roundingType !== 'taskTime' || isRounded) return task;

  const {
    originalStart,
    originalEnd,
    startTime,
    endTime,
  } = task;

  if (!originalStart && !originalEnd) return task;

  return {
    ...task,
    startTime: originalStart ?? startTime,
    endTime: originalEnd ?? endTime,
  };
};

/**
 * Manual OT + taskTime read-path rounding (mirrors AutoRounding day total logic).
 * Automatic OT companies get task-time rounding persisted on write; skip here.
 */
export const applyTaskTimeDayRounding = (tasks, settings, options = {}) => {
  if (settings?.roundingType !== 'taskTime') return tasks;
  return applyDayBucketRounding(tasks, settings, options);
};

/**
 * Manual OT + overallTime read-path rounding (mirrors backend roundDaysTask).
 * Automatic OT companies get day rounding persisted on write; skip here.
 */
export const applyOverallDayRounding = (tasks, settings, options = {}) => {
  if (settings?.roundingType === 'taskTime') return tasks;
  return applyDayBucketRounding(tasks, settings, options);
};

/**
 * Derives hour buckets from stored task segments.
 * Regular/OT/DT totals come from getRuntimes; Sat/Sun buckets are labeled from task date.
 */
export const deriveBuckets = (task, settings = {}) => {
  if (!task) return null;

  const {
    saturdayPay,
    saturdayOvertime,
    sundayPay,
    sundayOvertime,
    enableManualOT,
  } = settings;

  const {
    regularTime = 0,
    breakTime = 0,
    overtime = 0,
    doubleOT = 0,
    pto = 0,
  } = TaskHelpers.getRuntimes(task, false);

  const taskDate = TaskHelpers.getTaskDate(task);
  const dayOfWeek = taskDate?.weekday;
  const hasSaturdaySettings = (saturdayPay || saturdayOvertime) && !enableManualOT;
  const hasSundaySettings = (sundayPay || sundayOvertime) && !enableManualOT;
  const isSaturday = hasSaturdaySettings && dayOfWeek === 6;
  const isSunday = hasSundaySettings && dayOfWeek === 7;

  const base = {
    ...task,
    regularTime,
    breakTime,
    overtime,
    doubleOT,
    pto,
    saturdayTime: 0,
    saturdayOT: 0,
    saturdayDoubleOT: 0,
    sundayTime: 0,
    sundayOT: 0,
    sundayDoubleOT: 0,
    isSaturday: !!isSaturday,
    isSunday: !!isSunday,
  };

  if (isSaturday) {
    return {
      ...base,
      saturdayTime: regularTime,
      saturdayOT: overtime,
      saturdayDoubleOT: doubleOT,
      regularTime: 0,
      overtime: 0,
      doubleOT: 0,
    };
  }

  if (isSunday) {
    return {
      ...base,
      sundayTime: regularTime,
      sundayOT: overtime,
      sundayDoubleOT: doubleOT,
      regularTime: 0,
      overtime: 0,
      doubleOT: 0,
    };
  }

  return base;
};

/**
 * deriveBuckets + manual-OT overallTime day rounding.
 * Pass settingsDateCache when per-task-date OT settings apply (versioned exports);
 * otherwise a single settings object is used for all tasks.
 */
export const deriveTasksWithBuckets = ({
  tasks = [],
  settings = {},
  settingsDateCache,
  isRounded = true,
}) => {
  const byDate = {};
  tasks.forEach((task) => {
    const dateKey = TaskHelpers.getTaskDate(task)?.toISODate() ?? 'unknown';
    if (!byDate[dateKey]) byDate[dateKey] = [];
    byDate[dateKey].push(task);
  });

  const result = [];
  Object.entries(byDate).forEach(([dateKey, dayTasks]) => {
    const dateSettings = settingsDateCache?.[dateKey] ?? settings;
    const resolved = dayTasks.map((task) => resolveTaskDisplayTimes(task, {
      isRounded,
      roundingType: dateSettings.roundingType,
    }));
    const derived = resolved
      .map((task) => deriveBuckets(task, dateSettings))
      .filter(Boolean);
    result.push(...applyDayBucketRounding(derived, dateSettings, { isRounded }));
  });
  result.sort((a, b) => TaskHelpers.getStartTime(a) - TaskHelpers.getStartTime(b));
  return result;
};
